import moment from 'moment';
import format from 'date-fns/format';
import { QuestionTextField, QuestionYesNoField, QuestionSocialInsuranceNumberField, QuestionDateDropdownField, QuestionDropDownField } from 'components/Questionnaires/fields';
import { QuestionSignerBlock, QuestionRcUsTaxResidentBlock } from 'components/Questionnaires/blocks';
import { requiredValidator, lengthValidator, sequentialIntegerValidator, sameCharacterValidator, yearMonthValidator } from 'components/Questionnaires/validators';
import { residentAddressBlock, mailingAddressBlock, rcTaxResidencesBlock } from './blocks/blocks';

const rc518 = {
  blocks: [
    {
      id: 'name',
      title: 'questionnaire.rc518.name',
      info: {
        instructions: 'questionnaire.rc518.name.info'
      },
      visible: true,
      questions: [
        {
          id: 'firstName',
          label: 'questionnaire.rc518.name.firstName.label',
          component: QuestionTextField,
          className: 'col-12 col-lg-12',
          visible: true,
          lockGroup: 'name',
          validation: {
            conditions: [
              { condition: requiredValidator, message: 'questionnaire.rc518.name.firstName.errorMessage' }
            ]
          }
        },
        {
          id: 'middleName',
          label: 'questionnaire.rc518.name.middleName.label',
          component: QuestionTextField,
          className: 'col-12 col-lg-4',
          visible: true,
          lockGroup: 'name',
        },
        {
          id: 'lastName',
          label: 'questionnaire.rc518.name.lastName.label',
          component: QuestionTextField,
          className: 'col-12 col-lg-8',
          visible: true,
          lockGroup: 'name',
          validation: {
            conditions: [
              { condition: requiredValidator, message: 'questionnaire.rc518.name.lastName.errorMessage' }
            ]
          }
        },
      ]
    },
    residentAddressBlock(),
    mailingAddressBlock(),
    {
      id: 'canadianTaxResident',
      title: 'questionnaire.rc518.canadianTaxResident',
      visible: true,
      questions: [
        {
          id: 'isCanadianTaxResident',
          label: 'questionnaire.rc518.canadianTaxResident.isCanadianTaxResident.label',
          component: QuestionYesNoField,
          visible: true,
          validation: {
            conditions: [
              { condition: requiredValidator, message: 'questionnaire.rc518.canadianTaxResident.isCanadianTaxResident.errorMessage' }
            ]
          }
        },
        {
          id: 'socialInsuranceNumber',
          label: 'questionnaire.rc518.canadianTaxResident.socialInsuranceNumber.label',
          component: QuestionSocialInsuranceNumberField,
          componentProps: {
            length: 9
          },
          visible: true,
          info: {
            instructions: 'questionnaire.rc518.canadianTaxResident.socialInsuranceNumber.info'
          },
          validation: {
            conditions: [
              { condition: lengthValidator, message: 'questionnaire.rc518.canadianTaxResident.socialInsuranceNumber.errorMessage' },
              { condition: sequentialIntegerValidator, message: 'questionnaire.rc518.canadianTaxResident.socialInsuranceNumber.sequentialErrorMessage' },
              { condition: sameCharacterValidator, message: 'questionnaire.rc518.canadianTaxResident.socialInsuranceNumber.sameCharErrorMessage' }
            ]
          }
        },
        {
          id: 'residentSinceCanada',
          label: 'questionnaire.rc518.canadianTaxResident.residentSinceCanada.label',
          component: QuestionDateDropdownField,
          componentProps: {
            dateFormat: 'yyyy-MM',
            monthYearOnly: true,
            maxDate: format(moment().toDate(), 'yyyy-MM')
          },
          className: 'col-12 rc-resident-since-date',
          visible: bAnswers => bAnswers?.isCanadianTaxResident === 'Yes',
          info: {
            instructions: 'questionnaire.rc518.canadianTaxResident.residentSinceCanada.info'
          },
          validation: {
            conditions: [
              { condition: requiredValidator, message: 'questionnaire.rc.residentSince.requiredErrorMessage' },
              { condition: yearMonthValidator, message: 'questionnaire.rc518.canadianTaxResident.residentSinceCanada.errorMessage' }
            ]
          }
        }
      ]
    },
    {
      id: 'usTaxResident',
      title: 'questionnaire.rc518.usTaxResident',
      component: QuestionRcUsTaxResidentBlock,
      visible: true,
      componentProps: {
        tinType: 'SSN'
      }
    },
    rcTaxResidencesBlock(),
    {
      id: 'otherTaxResident',
      title: 'questionnaire.rc518.otherTaxResident',
      visible: formAnswers => Array.isArray(formAnswers?.taxResidences) && formAnswers.taxResidences.some(tr => tr?.country),
      questions: [
        {
          id: 'residentSinceOther',
          label: 'questionnaire.rc518.otherTaxResident.residentSinceOther.label',
          component: QuestionDateDropdownField,
          componentProps: {
            dateFormat: 'yyyy-MM',
            monthYearOnly: true,
            maxDate: format(moment().toDate(), 'yyyy-MM')
          },
          className: 'col-12 rc-resident-since-date',
          visible: (bAnswers, qAnswers) => Array.isArray(qAnswers?.taxResidences) && qAnswers.taxResidences.some(tr => tr?.country),
          info: {
            instructions: 'questionnaire.rc518.otherTaxResident.residentSinceOther.info'
          },
          validation: {
            conditions: [
              { condition: requiredValidator, message: 'questionnaire.rc.residentSince.requiredErrorMessage' },
              { condition: yearMonthValidator, message: 'questionnaire.rc518.otherTaxResident.residentSinceOther.errorMessage' }
            ]
          }
        }
      ]
    },
    {
      id: 'birth',
      title: 'questionnaire.rc518.birth',
      visible: true,
      info: {
        instructions: 'questionnaire.rc518.birth.info'
      },
      questions: [
        {
          id: 'date',
          label: 'questionnaire.rc518.birth.date.label',
          component: QuestionDateDropdownField,
          componentProps: {
            minDate: format(moment().subtract(125, 'years').toDate(), 'dd-MM-yyyy'),
            maxDate: format(moment().subtract(1, 'days').toDate(), 'dd-MM-yyyy'),
            dateFormat: 'dd-MM-yyyy',
            showToolTip: true
          },
          visible: true,
          validation: {
            conditions: [
              { condition: requiredValidator, message: 'questionnaire.rc518.birth.errorMessage' }
            ]
          }
        }
      ]
    },
    {
      id: 'controllingPerson',
      title: 'questionnaire.rc518.controllingPerson',
      visible: true,
      linkBlock: true,
      componentProps: {
        optional: true
      },
      info: {
        instructions: 'questionnaire.rc518.controllingPerson.info'
      },
      questions: [
        {
          id: 'type',
          label: 'questionnaire.rc518.controllingPerson.type.label',
          component: QuestionDropDownField,
          visible: true,
          optional: true,
          componentProps: {
            items: 'questionnaire.rc518.controllingPerson.type.items'
          },
        }
      ]
    },
    {
      id: 'signer',
      title: 'questionnaire.signer',
      component: QuestionSignerBlock,
      componentProps: {
        individualOnly: true,
        capacityInfo: 'questionnaire.rc518.signer.capacity.info'
      },
      visible: true
    },
  ]
};

export default rc518;
